import pageType from 'Addons/search/pageType';
import type { SelectionEntry } from 'Models/index';
import requestConfig from 'Models/uiConfig/requestConfig';
import type { NormalizedSearchRequest, SearchRequestDefaults } from 'Models/uiConfig/requestTypes';
import uiConfig from 'Models/uiConfig/uiConfig';
import { isTruthy } from 'Utils/guards';

import { normalizeParsedSearchRequest } from './requestNormalization';
import { uriDecode, uriDecodeTerm } from './uriEncoding';

const { defaultSort, defaultPageSize } = requestConfig;

const requestDefaults: SearchRequestDefaults = {
  catalog: '',
  filterQuery: '',
  pageNumber: 0,
  pageSize: defaultPageSize,
  query: '',
  selection: [],
  sort: defaultSort,
};

export const defaultRequest = normalizeParsedSearchRequest({}, requestDefaults);

type UriSearchHash = { search?: string; hash?: string };

type UriPart = {
  rawSelection: string;
  params: Record<string, string>;
  otherParams: string[];
};

export default function getRequestFromUri(location: UriSearchHash): NormalizedSearchRequest {
  const parser = uiConfig.customUriParser;

  const request =
    parser?.parsingNeeded?.(uiConfig.pageType) && typeof parser.parse === 'function'
      ? parser.parse(uiConfig.pageType)
      : getRequestFromUriParts(parseUri(location));

  return normalizeParsedSearchRequest(request, requestDefaults);
}

type UriParsedRequest = Pick<
  SearchRequestDefaults,
  'catalog' | 'filterQuery' | 'pageNumber' | 'pageSize' | 'query' | 'selection' | 'sort'
>;

export function getRequestFromUriParts({
  rawSelection,
  params,
}: Pick<UriPart, 'rawSelection' | 'params'>): UriParsedRequest {
  let selectionFromParams = '';

  const pageNumber = requestConfig.pageUriParam.toLowerCase();
  const canReadQuery = uiConfig.pageType !== pageType.category && uiConfig.pageType !== pageType.brand;

  const request = Object.entries(params).reduce<UriParsedRequest>((req, [name, rawValue]) => {
    switch (name) {
      case 'facet':
        selectionFromParams = rawValue;
        break;
      case 'filterquery':
        req.filterQuery = uriDecode(rawValue);
        break;
      case 'sort':
        req.sort = decodeURIComponent(rawValue);
        break;
      case pageNumber:
      case 'page':
      case 'pagenumber': {
        const parsed = Number(rawValue);
        if (Number.isFinite(parsed)) {
          req.pageNumber = parsed - 1;
        }
        break;
      }
      case 'pagesize':
      case 'showed': {
        if (rawValue === 'all') {
          req.pageSize = 'all';
        } else {
          const parsed = Number(rawValue);
          if (Number.isFinite(parsed)) {
            req.pageSize = parsed;
          }
        }
        break;
      }
      case 'query':
        if (canReadQuery && !('q' in params)) {
          req.query = uriDecode(rawValue ?? '');
        }
        break;
      case 'q':
        if (canReadQuery && !('query' in params)) {
          req.query = decodeURIComponent((rawValue ?? '').replace(/\+/g, ' '));
        }
        break;
      case 'catalog':
        req.catalog = uriDecode(rawValue);
        break;
    }
    return req;
  }, {});

  request.selection = prepareSelection([rawSelection, selectionFromParams].filter(isTruthy).join('/'));
  return request;
}

export function parseUri({ search, hash }: UriSearchHash): Pick<UriPart, 'rawSelection' | 'params'> & {
  search: UriPart;
  hash: UriPart;
} {
  function parse(s: string) {
    const split = s.replace(/[?#]/, '').split('&');

    const rawSelection = looksLikeSelection(split[0] || '');
    if (rawSelection) {
      split.shift();
    }
    const params = split.reduce((p: Record<string, string>, part) => {
      const [key, value] = part.split('=');
      if (key) {
        p[key.toLowerCase()] = value;
      }
      return p;
    }, {});
    const otherParams = split.filter(isTruthy);
    return { rawSelection, params, otherParams };
  }
  const searchPart = parse(search || '');
  const hashPart = parse(hash || '');
  const hashSelection = hashPart.rawSelection || rawSelectionFromHash(hashPart.params);

  return {
    rawSelection: searchPart.rawSelection || hashSelection,
    params: { ...searchPart.params, ...hashPart.params },
    search: searchPart,
    hash: { ...hashPart, rawSelection: hashSelection },
  };
}

export function looksLikeSelection(param: string): string {
  const separatorIndex = param.indexOf('=');
  if (separatorIndex >= 0 && separatorIndex < param.length - 1) {
    return '';
  }

  if (param.includes('/') && !param.includes('=')) {
    return param;
  }
  let decoded: string;
  try {
    decoded = decodeURIComponent(param);
  } catch (error) {
    // Malformed foreign parameters are not URI selections.
    if (error instanceof URIError) {
      return '';
    }
    throw error;
  }
  if (decoded.includes('/') && (!decoded.includes('=') || decoded.endsWith('='))) {
    return decoded.replace('=', '');
  }
  return '';
}

function rawSelectionFromHash(hParams: Record<string, string>) {
  const facets: string[] = [];
  for (let i = 0; ; i++) {
    const field = hParams[`fld${i}`];
    const sel = hParams[`sel${i}`];
    if (field && sel) {
      facets.push(`${field}/${sel}`);
    } else {
      break;
    }
  }
  return facets.join('/');
}

function prepareSelection(raw: string) {
  const selection: SelectionEntry[] = [];

  const split = raw.split('/').filter(isTruthy);
  for (let i = 0; i < split.length; i += 2) {
    const fieldRaw = split[i];
    const termRaw = split[i + 1];
    if (typeof fieldRaw !== 'string' || typeof termRaw !== 'string') {
      continue;
    }
    const field = uriDecode(fieldRaw);
    const term = uriDecodeTerm(termRaw);
    if (!field || !term) {
      continue;
    }

    selection.push({ field, term });
  }
  return selection;
}
